// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import GoogleMapReact from 'google-map-react';

// Components
import Place from './Place';
import PlaceDescription from './PlaceDescription';
import UserLocation from './UserLocation';

// Actions
import { updateMapViewportProperties, selectFeature } from '../actions';

// Shapes
import {
  coordinateShape, layerShape, featureShape, projectCategoryFromCSSShape
} from '../propTypesShapes';
import { userLocationShape, profileShape } from '../../authentication/propTypesShapes';
import { gMapTypeIdType } from '../../settings/propTypesShapes';

// Utils
import { fitBounds } from 'google-map-react/utils';
import {
  LocationControl, ZoomControl,
  calculateBoundsForFeatures, isValidBounds,
  calculateZoomByLatitudeAndDesiredMeters, calculateZoomByLatitudeAndMilesPerInch
} from '../utils/map';
import { createPopupInstance } from '../utils/popup/popup';
import { isFirstRender } from '../../shared/utils/data';
//import { logToConsole } from '../../shared/utils/log';

// Assets
import './Map.css';
import '../utils/popup/popup.css';

// Constants
import { LEFT_PANE } from '../constants';


const DEFAULT_ZOOM_IN_MILES_PER_INCH = 10;

const { REACT_APP_GOOGLE_MAPS_API_KEY } = process.env;


function updateViewportProperties(viewportProperties, dataLayer) {
  const { features } = dataLayer;
  const bounds = calculateBoundsForFeatures(features);
  let size = viewportProperties.size;

  // It only is executed the first time that the Map is shown (Initialize a hidden map)
  if (size.width === 0 || size.height === 0) {
    const domRec = document.getElementById('Map').getBoundingClientRect();
    size = { width: domRec.width, height: domRec.height };
  }

  const { center, zoom } = fitBounds(bounds, size);
  let uniqueFeatureCenter = null, uniqueFeatureZoom = null;

  // Center the map in the coordinates of the unique feature contained in the layer
  if (features.length === 1 || !isValidBounds(bounds)) {
    const { position } = features[0];

    uniqueFeatureCenter = position;
    uniqueFeatureZoom = calculateZoomByLatitudeAndMilesPerInch(
      position.lat, DEFAULT_ZOOM_IN_MILES_PER_INCH
    );
  }

  return {
    marginBounds: bounds,
    bounds,
    center: uniqueFeatureCenter || center,
    zoom: uniqueFeatureZoom || zoom,
    size
  };
}

const createMapOptions = mapTypeId => {
  return {
    fullscreenControl: false,
    zoomControl: false,
    // Enabling zoom and pan interaction on desktop, touchscreen, and mobile devices
    // using the scroll wheel on a mouse, the zoom controls, a trackpad/mouse, or
    // a touchscreen.
    gestureHandling: 'greedy',
    scaleControl: true,
    mapTypeId
  };
};


class Map extends React.Component {
  static propTypes = {
    token: PropTypes.string,
    profile: profileShape,
    userLocation: userLocationShape,
    projectCategoryFromCSS: projectCategoryFromCSSShape,
    location: PropTypes.object,
    isOnMobileView: PropTypes.bool,
    defaultCenter: coordinateShape.isRequired,
    defaultZoom: PropTypes.number,
    currentLayer: layerShape,
    mapTypeId: gMapTypeIdType,
    currentFeature: featureShape
  };

  constructor(props) {
    super(props);

    this.state = {
      google: null,
      viewportProperties: null,
      isUserLocationShown: false
    };
    this.infoWindow = null;

    this.handleOnChange = this.handleOnChange.bind(this);
    this.handleOnChildClick = this.handleOnChildClick.bind(this);
    this.handleOnGoogleApiLoaded = this.handleOnGoogleApiLoaded.bind(this);
    this.handleLocationControlOnClick = this.handleLocationControlOnClick.bind(this);
    this.handleZoomInControlOnClick = this.handleZoomInControlOnClick.bind(this);
    this.handleZoomOutControlOnClick = this.handleZoomOutControlOnClick.bind(this);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { currentLayer, currentFeature, dispatch, profile } = newProps;
    const { viewportProperties } = this.state;

    if (currentLayer !== this.props.currentLayer && currentLayer && currentLayer.dataLayer
      && !isFirstRender(currentLayer.dataLayer.features)) {
      this.setState({
        viewportProperties: updateViewportProperties(viewportProperties, currentLayer.dataLayer)
      });
    }

    if (currentFeature !== this.props.currentFeature) {
      if (currentFeature) {
        const { map } = this.state.google;
        const { position } = currentFeature;

        map.setCenter(position);
        map.setZoom(calculateZoomByLatitudeAndMilesPerInch(position.lat, DEFAULT_ZOOM_IN_MILES_PER_INCH));
        this.infoWindow.setContent(
          <PlaceDescription
            feature={currentFeature}
            dispatch={dispatch}
            profile={profile}
          />
        );
        this.infoWindow.setPosition(position);
        this.infoWindow.open(); //map);
      } else {
        this.infoWindow.close();
      }
    }
  }

  shouldComponentUpdate(newProps, newState) {
    const { currentFeature } = newProps;

    if (currentFeature !== this.props.currentFeature) {
      return false;
    }

    return true;
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      currentLayer, currentFeature, location, dispatch, profile
    } = this.props;
    const { viewportProperties } = this.state;
    const dataLayer = currentLayer?.dataLayer;

    // Only for mobile view
    if ((location.state !== prevProps.location.state
      && location.state?.fromBottomMenuBar && location.state.selectedMapOption)
      && !isFirstRender(dataLayer?.features) ) {
      const newViewportProperties = updateViewportProperties(viewportProperties, dataLayer);

      // Center the map in the coordinates of the selected feature
      if (currentFeature) {
        const { position } = currentFeature;
        newViewportProperties.center = position;
        newViewportProperties.zoom = calculateZoomByLatitudeAndMilesPerInch(
          position.lat, DEFAULT_ZOOM_IN_MILES_PER_INCH
        );
      }

      this.initializeViewportTimeout = setTimeout(() => {
        // Refresh the pin popover if a selected feature exists
        if (currentFeature) {
          this.infoWindow.setContent(
            <PlaceDescription
              feature={currentFeature}
              dispatch={dispatch}
              profile={profile}
            />
          );
          this.infoWindow.open(); //google.map);
        }

        this.props.dispatch(updateMapViewportProperties(newViewportProperties));
        this.setState(() => ({ viewportProperties: newViewportProperties }));
      }, 200);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.initializeViewportTimeout);
  }

  handleOnChange(newViewportProperties) {
    const { currentFeature, dispatch } = this.props;
    const { viewportProperties } = this.state;

    // Zoom In Action when InfoWindow is opened
    if ( currentFeature && newViewportProperties && viewportProperties &&
      // > Zoom In or < Zoom out
      newViewportProperties.zoom !== viewportProperties.zoom &&
      this.infoWindow && this.infoWindow.isOpen() ) {
      const { map } = this.state.google;

      map.panTo(currentFeature.position); //this.infoWindow.getPosition() );
      this.infoWindow.open(); //map);
    }

    dispatch(updateMapViewportProperties(newViewportProperties));
    this.setState({ viewportProperties: newViewportProperties });
  }

  showOrganizationsList() {
    const { isOnMobileView, history, location } = this.props;

    if ( !isOnMobileView ) {
      if (location.hash !== LEFT_PANE.HASH_LINK.LISTING) {
        history.push(`/${LEFT_PANE.HASH_LINK.LISTING}`);
      }
    }
  }

  handleOnChildClick(key, childProps) {
    const { token, profile, currentLayer, currentFeature, dispatch } = this.props;
    const featureIndex = parseInt(key, 10);

    if (featureIndex >= 0) {
      const feature = currentLayer.dataLayer.features[featureIndex];

      if ( currentFeature && currentFeature.id === feature.id ) {
        if (!this.infoWindow.isOpen()) {
          this.infoWindow.open(); //this.state.google.map);
        }
      } else {
        dispatch(selectFeature(token, profile, currentLayer.mapLayer, feature));
        this.showOrganizationsList();
      }
    }
  }

  handleLocationControlOnClick() {
    this.setState({ isUserLocationShown: true }, () => {
      const { latitude, longitude } = this.props.userLocation;
      const { map } = this.state.google;
      map.panTo({ lat: latitude, lng: longitude });
    });
  }

  handleZoomInControlOnClick() {
    const { map } = this.state.google;
    map.setZoom(map.getZoom() + 1);
  }

  handleZoomOutControlOnClick() {
    const { map } = this.state.google;
    map.setZoom(map.getZoom() - 1);
  }

  handleOnGoogleApiLoaded(google) {
    const { map, maps } = google;

    // Add method to verify if InfoWindow is opened
    // maps.InfoWindow.prototype.isOpen = function() {
    //   var map = this.getMap();
    //   return (map !== null && typeof map !== 'undefined');
    // };
    //this.infoWindow = new maps.InfoWindow();

    this.infoWindow = createPopupInstance(maps, map);

    // Create the DIV to hold the custom controls.
    const locationControlDiv = LocationControl(2, this.handleLocationControlOnClick);
    const zoomControlDiv = ZoomControl(1, this.handleZoomInControlOnClick, this.handleZoomOutControlOnClick);

    map.controls[maps.ControlPosition.RIGHT_BOTTOM].push(locationControlDiv);
    map.controls[maps.ControlPosition.RIGHT_BOTTOM].push(zoomControlDiv);

    this.setState({ google });
  }

  render() {
    const {
      userLocation, isOnMobileView, defaultCenter, defaultZoom, mapTypeId,
      currentLayer, className = '', projectCategoryFromCSS //, location
    } = this.props;
    const { viewportProperties, isUserLocationShown } = this.state;
    const dataLayer = currentLayer?.dataLayer;
    const center = viewportProperties?.center || {
      lat: defaultCenter.latitude, lng: defaultCenter.longitude
    };
    const zoom = viewportProperties?.zoom || defaultZoom
      || calculateZoomByLatitudeAndDesiredMeters(defaultCenter.latitude, 100000);
    const resetBoundsOnResize = isOnMobileView; // && location.state && location.state.fromBottomMenuBar;

    let layerIcon = undefined;
    let defaultIcon = '/assets/icons/RippleNami_Pin.png';

    if (projectCategoryFromCSS && currentLayer) {
      const { layerIds, cssFileName } = projectCategoryFromCSS;
      const { mapLayer } = currentLayer;

      defaultIcon = `/assets/icons/${cssFileName}/default_pin.png`;

      if (layerIds.includes(mapLayer.id)) {
        layerIcon = `/assets/icons/${cssFileName}/layers/${mapLayer.name}.png`;
      } else {
        layerIcon = defaultIcon;
      }
    }

    //logToConsole('Current Data Layer: ', dataLayer);

    return (
      <div className={`Map ${className}`} id="Map">
        <GoogleMapReact
          bootstrapURLKeys={{ key: REACT_APP_GOOGLE_MAPS_API_KEY }}
          options={(maps) => createMapOptions(mapTypeId)}
          center={center}
          zoom={zoom}
          onChange={this.handleOnChange}
          onChildClick={this.handleOnChildClick}
          onGoogleApiLoaded={this.handleOnGoogleApiLoaded}
          yesIWantToUseGoogleMapApiInternals
          resetBoundsOnResize={resetBoundsOnResize}
        >
          {isUserLocationShown &&
            <UserLocation
              key={-1}
              lat={userLocation.latitude}
              lng={userLocation.longitude}
              text="My location"
            />
          }

          {dataLayer?.features.map((feature, index) => {
            const {
              position: { lat, lng },
              properties: { name, icon }
            } = feature;

            return (
              <Place
                key={index}
                lat={lat}
                lng={lng}
                text={name}
                icon={layerIcon || icon}
                defaultIcon={defaultIcon}
              />
            );
          })}
        </GoogleMapReact>
      </div>
    );
  }
}

Map.defaultProps = {
  isOnMobileView: false,
  defaultCenter: {
    latitude: 33.4378,
    longitude: -117.6203
  },
  defaultZoom: null
};

export default connect(({ authentication, home, settings }) => ({
  token: authentication.token,
  profile: authentication.profile,
  userLocation: authentication.userLocation,
  currentLayer: home.currentLayer,
  mapTypeId: settings.mapTypeId,
  currentFeature: home.currentFeature,
  projectCategoryFromCSS: home.projectCategoryFromCSS
}), null)(Map);
